import { useEffect, useState } from "react"
import classNames from "classnames"
import { ZoomInOutlined } from "@ant-design/icons"

import { cdn } from "core/cdn"
import styles from "./PhotoCarousel.module.scss"

interface PreviewModalProps {
  src: string
  caption: string
  open: boolean
  handleClose: () => void
  wide?: boolean
}

const PreviewModal = (props: PreviewModalProps): JSX.Element => {
  const { caption, src, open, handleClose, wide } = props

  return (
    <>
      {open && (
        <div
          id="myModal"
          className={styles.previewModal}
          onClick={handleClose}
        >
          <img
            className={classNames(
              styles.modalContent,
              wide ? styles.modalContentWide : null,
            )}
            src={src}
            alt=""
          />
          <div id={styles.caption}>{caption}</div>
        </div>
      )}
    </>
  )
}

interface PhotoCarouselProps {
  /** Shots to choose from. The 3D layout is built for three of them. */
  images: string[]
  selectedIndex: number
  onSelect: (index: number) => void
  alt?: string
  /** Portrait cards fit document scans, landscape ones fit 16:9 camera shots. */
  orientation?: "portrait" | "landscape"
}

/**
 * Lets the user pick one shot out of a burst. Tapping an image selects it,
 * tapping the selected one (or the magnifier) opens it fullscreen. The caller
 * owns the surrounding screen and the action buttons.
 */
export const PhotoCarousel = (props: PhotoCarouselProps): JSX.Element => {
  const {
    images,
    selectedIndex,
    onSelect,
    alt = "Aperçu",
    orientation = "portrait",
  } = props
  const [openModal, setOpenModal] = useState(false)

  // Every item positions itself against this custom property, so it has to
  // follow the selected index.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--positionView",
      `${selectedIndex + 1}`,
    )
  }, [selectedIndex])

  const select = (index: number) => {
    const last = images.length - 1
    let next = index
    if (next > last) next = 0
    if (next < 0) next = last
    onSelect(next)
  }

  const showWide = () => setOpenModal(true)

  // Landscape layout: the selected card in front, the remaining shots trailing
  // to its right, the ones already passed sliding away to the left.
  const landscape = orientation === "landscape"
  const stackVars = (index: number): React.CSSProperties => {
    const pos = index - selectedIndex
    return {
      "--pos": pos,
      "--dist": Math.abs(pos),
      "--dir": Math.sign(pos),
    } as React.CSSProperties
  }

  return (
    <>
      <PreviewModal
        src={images[selectedIndex]}
        caption={`Image ${selectedIndex + 1}`}
        open={openModal}
        handleClose={() => setOpenModal(false)}
        wide={landscape}
      />
      <div
        className={classNames(
          styles.PreviewCardContent,
          landscape ? styles.landscape : null,
        )}
      >
        <button onClick={() => select(selectedIndex - 1)}>
          <img src={cdn("images/btn-arrow-left.svg")} alt="left arrow" />
        </button>
        <div className={styles.carousel}>
          {images.map((img, index) => {
            return (
              <button
                key={index}
                className={classNames(
                  styles.item,
                  index === selectedIndex ? styles.ImageSelected : null,
                )}
                style={landscape ? stackVars(index) : undefined}
                onClick={() =>
                  index === selectedIndex ? showWide() : select(index)
                }
                onDoubleClick={showWide}
              >
                <img className={styles.PreviewPageImage} src={img} alt={alt} />
                {index === selectedIndex && (
                  <ZoomInOutlined
                    style={{
                      position: "absolute",
                      top: "15px",
                      right: "15px",
                      fontSize: "30px",
                      color: "white",
                    }}
                    onClick={showWide}
                  />
                )}
              </button>
            )
          })}
        </div>
        <button onClick={() => select(selectedIndex + 1)}>
          <img src={cdn("images/btn-arrow-right.svg")} alt="right arrow" />
        </button>
      </div>
    </>
  )
}

export default PhotoCarousel
